import React, { useEffect, useRef } from "react";
import { HERO } from "../data/copy";

/**
 * HERO — scroll-scrubbed cinematic 3D sequence (the centrepiece).
 * Loads a 120-frame render (rotate -> lid opens -> mint strip rises) and draws
 * it to a <canvas>. Scroll position picks the frame via an eased mapping, so the
 * sequence plays forward on scroll-down and REVERSES on scroll-up.
 * Phase 1 (0->0.20): headline fades + drifts up.
 * Phase 2 (0.16->1): box fades in + scales + scrubs through the 120 frames.
 * Progressive loading: first frame paints as soon as it arrives, later frames
 * draw as they load. Reduced-motion shows a static open-box frame (45).
 * No fade-in image, no autoplay video.
 */
const FRAME_COUNT = 120;
const REDUCED_FRAME = 45; // open-box hero frame for prefers-reduced-motion
const framePath = (i) => `/assets/frames/frame_${String(i).padStart(3, "0")}.webp?v=3`;

export const Hero = () => {
  const heroRef = useRef(null);
  const canvasRef = useRef(null);
  const typeRef = useRef(null);
  const cueRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const frames = [];

    const sizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
    };

    const draw = (frameIndex) => {
      const img = frames[Math.max(0, Math.min(FRAME_COUNT - 1, Math.round(frameIndex)))];
      if (!img || !img.complete) return;
      if (canvas.width === 0) sizeCanvas();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // contain-fit the frame in the canvas
      const s = Math.min(canvas.width / img.width, canvas.height / img.height);
      const w = img.width * s;
      const h = img.height * s;
      ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
    };

    const onScroll = () => {
      const hero = heroRef.current;
      const htype = typeRef.current;
      const cue = cueRef.current;
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      const scrollable = hero.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return;
      // p = 0 at top of hero, 1 at bottom of its scroll range
      const p = Math.max(0, Math.min(-rect.top / scrollable, 1));

      // PHASE 1 (0 -> .20): headline fades + drifts up
      const t1 = Math.min(p / 0.2, 1);
      // PHASE 2 (.16 -> 1): box fades in and the sequence scrubs
      const t2 = Math.max(0, Math.min((p - 0.16) / 0.84, 1));

      if (reduce) {
        htype.style.opacity = "1";
        htype.style.transform = "translate(-50%, -50%)";
        if (cue) cue.style.opacity = "0";
        canvas.style.opacity = "1";
        canvas.style.transform = "scale(1)";
        draw(REDUCED_FRAME); // static open-box frame, no scrub
        return;
      }

      htype.style.opacity = String(1 - t1);
      htype.style.transform = `translate(-50%, calc(-50% - ${t1 * 90}px))`;
      if (cue) cue.style.opacity = String(1 - t1);

      canvas.style.opacity = String(t2);
      canvas.style.transform = `scale(${0.92 + t2 * 0.28})`;
      // ease the scrub so each act (rotate / open / rise) lands deliberately
      const eased = t2 < 0.5 ? 2 * t2 * t2 : 1 - Math.pow(-2 * t2 + 2, 2) / 2;
      draw(eased * (FRAME_COUNT - 1)); // <-- the scrub: scroll drives the frame
    };

    // load the frame sequence progressively — draw the current target as each
    // frame arrives so the hero is never blank and never waits for all 120.
    for (let i = 0; i < FRAME_COUNT; i++) {
      const img = new Image();
      if (i < 10) img.fetchPriority = "high"; // prioritise the opening frames
      img.src = framePath(i);
      img.onload = () => {
        onScroll();
      };
      frames.push(img);
    }

    let raf = 0;
    const onScrollThrottled = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        onScroll();
      });
    };
    const onResize = () => {
      sizeCanvas();
      onScroll();
    };

    sizeCanvas();
    onScroll();
    window.addEventListener("scroll", onScrollThrottled, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScrollThrottled);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="hero" ref={heroRef} className="mk-hero" data-testid="hero-section">
      <div className="mk-stage">
        <h1 id="heroType" ref={typeRef} className="mk-hero__headline" data-testid="hero-headline">
          {HERO.line1}
          <br />
          {HERO.line2}
        </h1>
        {/* ASSET SLOT: 120-frame cinematic sequence (rotate → lid opens → strip
            rises), scroll-scrubbed. Procedural placeholder — swap with the
            commissioned photoreal render at frame_000.webp … frame_119.webp. */}
        <canvas
          id="boxCanvas"
          ref={canvasRef}
          className="mk-hero__canvas"
          data-testid="hero-box"
        />
        <div className="mk-scrollcue" ref={cueRef} data-testid="scroll-cue">
          SCROLL
        </div>
      </div>
    </section>
  );
};

export default Hero;
